import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import logo from "@/assets/logo.png";
import api from "@/services/api.js";
import { HiCheckCircle, HiExternalLink, HiOutlineInformationCircle, HiXCircle } from "react-icons/hi";
import { useLoading } from "@/context/LoadingContext.jsx";
import { useSearchParams, useNavigate } from "react-router-dom";

const Confirmation = () => {
  const navigate = useNavigate();
  const [admission, setAdmission] = useState(null);
  const { startLoading, stopLoading } = useLoading();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const id = searchParams.get("id");

  const guidelinesWithStagePre = useMemo(
    () => admission?.guidelines
      ?.filter((g) => g.canShowInPreRegistration)
      ?.map((g) => g.guideline)
      ?.filter(Boolean),
    [admission],
  );
  const isPatientAtLaboratory = useMemo(() => !!admission?.checkInDate && admission?.status !== "ARCHIVED", [admission?.checkInDate, admission?.status]);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      startLoading();
      const res = await api.get(`/admission/?id=${id}&token=${token}`);
      if (!res.ok) throw res;

      setAdmission(res.data);
    } catch (e) {
      if (e?.code === "UNAUTHORIZED") return navigate("/");
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      stopLoading();
    }
  };

  if (isPatientAtLaboratory) return <ValidateAdmissionAtLaboratory />;
  if (admission?.type === "LATER" && admission?.status === "VALIDATED") return <ValidatedAtHome guidelinesWithStagePre={guidelinesWithStagePre} />;
  if (admission?.type === "LATER" && ["TO_VALIDATE", "ON_HOLD"].includes(admission?.status)) return <SubmittedAtHome />;

  return <RejectAdmission rejectReason={admission?.rejectReason} />;
};

const SubmittedAtHome = () => {
  return (
    <div className="container mx-auto min-w-full min-h-screen flex flex-col font-sans p-5 pb-36">
      <div className="mt-5">
        <img src={logo} alt="Logo" className="mx-auto h-12" />
      </div>
      <div className="container mx-auto flex justify-center items-center font-sans flex-col grow max-w-3xl mt-8">
        <div className="flex flex-col items-center justify-center">
          <HiCheckCircle className="text-6xl text-primary mx-auto mb-6" />
          <h2 className="text-xl font-semibold mb-4 text-center">Votre demande a bien été reçue !</h2>
        </div>
        <p>Un email de réception vous a été envoyé.</p>
        <p className="text-sm text-red-500">(L'email peut être dans vos spams)</p>
        <div className="p-4 rounded-lg mt-4 border-blue-500 border-2 bg-blue-500/10 flex items-center gap-4 w-full">
          <HiOutlineInformationCircle className="text-2xl text-blue-500 flex-shrink-0" />
          <div>
            <span className="font-semibold text-blue-500">En cours d'examen : </span>
            Notre équipe va examiner votre demande et vous recevrez un email de confirmation sous 1 jour ouvré, avec les instructions pour signaler votre présence au laboratoire.
          </div>
        </div>
      </div>
    </div>
  );
};

const ValidatedAtHome = ({ guidelinesWithStagePre }) => {
  return (
    <div className="container mx-auto min-w-full min-h-screen flex flex-col font-sans p-5 pb-36">
      <div className="mt-5">
        <img src={logo} alt="Logo" className="mx-auto h-12" />
      </div>
      <div className="container mx-auto flex justify-center items-center font-sans flex-col grow max-w-3xl mt-8">
        <div className="flex flex-col items-center justify-center">
          <HiCheckCircle className="text-6xl text-primary mx-auto mb-6" />
          <h2 className="text-xl font-semibold mb-4 text-center">Votre demande est confirmée !</h2>
        </div>
        <p>Un email de confirmation vous a été envoyé avec l'ensemble des informations.</p>
        <p className="text-sm text-red-500">(L'email peut être dans vos spams)</p>
        {guidelinesWithStagePre?.length > 0 && (
          <div className="bg-gray-50 p-4 rounded-lg mt-4 w-full border-primary border-2 bg-primary/10">
            <div className="flex gap-4 flex-col">
              <div className="font-semibold text-primary">Informations importantes :</div>
              <div className="flex flex-col gap-2">
                {guidelinesWithStagePre.map((step, index) => (
                  <div key={index} className="flex items-start">
                    <span className="text-primary mr-1 flex-shrink-0">✔︎</span>
                    <div className="flex-1">
                      <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 align-baseline text-primary-600 hover:text-primary-800 underline">
                              <HiExternalLink className="w-4 h-4 flex-shrink-0" />
                              {props.children}
                            </a>
                          ),
                        }}
                      >
                        {step}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="p-4 rounded-lg mt-4 border-green-500 border-2 bg-green-500/10 flex items-center gap-4 w-full">
          <HiOutlineInformationCircle className="text-2xl text-green-500 flex-shrink-0" />
          <div>
            <span className="font-semibold text-green-500">Prochaine étape : </span>
            Présentez-vous au laboratoire et scannez le QR code présent sur le mur à partir du lien "je signale ma présence" présent sur votre email.
          </div>
        </div>
      </div>
    </div>
  );
};

const ValidateAdmissionAtLaboratory = () => {
  return (
    <div className="container mx-auto min-w-full min-h-screen flex flex-col font-sans p-5 pb-36">
      <div className="mt-5">
        <img src={logo} alt="Logo" className="mx-auto h-12" />
      </div>
      <div className="container mx-auto flex justify-center items-center font-sans flex-col grow max-w-3xl">
        <div className="flex flex-col items-center justify-center">
          <HiCheckCircle className="text-6xl text-primary mx-auto mb-6" />
          <h2 className="text-xl font-semibold mb-4 text-center">Votre demande a bien été prise en compte</h2>
        </div>
        <div className="p-4 rounded-lg mt-4 border-green-500 border-2 bg-green-500/10 flex items-center gap-4 w-full">
          <HiOutlineInformationCircle className="text-2xl text-green-500 flex-shrink-0" />
          <div>
            <span className="font-semibold text-green-500">Prochaine étape : </span>
            Installez-vous dans la salle d'attente, un préleveur viendra vous chercher dès que possible.
          </div>
        </div>
      </div>
    </div>
  );
};

const RejectAdmission = ({ rejectReason }) => {
  return (
    <div className="container mx-auto min-w-full min-h-screen flex flex-col font-sans p-5 pb-36">
      <div className="mt-5">
        <img src={logo} alt="Logo" className="mx-auto h-12" />
      </div>
      <div className="container mx-auto flex justify-center items-center font-sans flex-col grow">
        <div className="max-w-3xl flex flex-col items-center justify-center">
          <>
            <div className="flex flex-col items-center justify-center">
              <HiXCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h2 className="text-xl font-semibold mb-4 text-center">Votre présence n'a pas pu être prise en compte</h2>
            </div>
            <div className="p-4 rounded-lg mt-4 border-red-500 border-2 bg-red-500/10 flex items-center gap-4">
              <HiOutlineInformationCircle className="text-2xl text-red-500" />
              <div>Présentez-vous au secrétariat du laboratoire pour obtenir de l'aide.</div>
            </div>
            <p className="text-gray-500 mt-4">{rejectReason || "Motif du refus non spécifié"}</p>
          </>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
